import { supabase } from '../supabase';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { TicketCategoryManager, TicketType } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

interface SupabaseTicketCategoryManager {
  id: string;
  ticket_type: TicketType;
  pay_class: 'UK' | 'FP';
  manager_ids: string[];
  role_ids: string[];
  created_at: string;
  updated_at: string;
}

const convertFromSupabase = (data: SupabaseTicketCategoryManager): TicketCategoryManager => {
  return {
    id: data.id,
    ticket_type: data.ticket_type,
    pay_class: data.pay_class,
    manager_ids: data.manager_ids || [],
    role_ids: data.role_ids || [],
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' })
  };
};

const convertToSupabase = (data: Omit<TicketCategoryManager, 'id' | 'created_at' | 'updated_at'>) => {
  return {
    ticket_type: data.ticket_type,
    pay_class: data.pay_class,
    manager_ids: data.manager_ids || [],
    role_ids: data.role_ids || []
  };
};

const SupabaseTicketCategoryManagerService = {
  async getAll(): Promise<TicketCategoryManager[]> {
    try {
      const { data, error } = await supabase
        .from('ticket_category_managers')
        .select('*')
        .order('ticket_type', { ascending: true });

      if (error) throw error;

      return (data || []).map(convertFromSupabase);
    } catch (error) {
      console.error('Error fetching ticket category managers:', error);
      return [];
    }
  },

  async getByType(ticketType: TicketType): Promise<TicketCategoryManager[]> {
    try {
      const { data, error } = await supabase
        .from('ticket_category_managers')
        .select('*')
        .eq('ticket_type', ticketType)
        .order('pay_class', { ascending: true });

      if (error) throw error;

      return (data || []).map(convertFromSupabase);
    } catch (error) {
      console.error('Error fetching ticket category managers by type:', error);
      return [];
    }
  },

  async getById(id: string): Promise<TicketCategoryManager | null> {
    try {
      const { data, error } = await supabase
        .from('ticket_category_managers')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return convertFromSupabase(data);
    } catch (error) {
      console.error('Error fetching ticket category manager:', error);
      return null;
    }
  },

  async create(data: Omit<TicketCategoryManager, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    try {
      const { data: result, error } = await supabase
        .from('ticket_category_managers')
        .insert(convertToSupabase(data))
        .select()
        .single();

      if (error) throw error;

      return result.id;
    } catch (error) {
      console.error('Error creating ticket category manager:', error);
      throw error;
    }
  },

  async update(id: string, data: Partial<Omit<TicketCategoryManager, 'id' | 'created_at' | 'updated_at'>>): Promise<string> {
    try {
      const update_data: any = {};

      if (data.ticket_type !== undefined) update_data.ticket_type = data.ticket_type;
      if (data.pay_class !== undefined) update_data.pay_class = data.pay_class;
      if (data.manager_ids !== undefined) update_data.manager_ids = data.manager_ids;
      if (data.role_ids !== undefined) update_data.role_ids = data.role_ids;

      const { error } = await supabase
        .from('ticket_category_managers')
        .update(update_data)
        .eq('id', id);

      if (error) throw error;

      return id;
    } catch (error) {
      console.error('Error updating ticket category manager:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('ticket_category_managers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting ticket category manager:', error);
      return false;
    }
  }
};

export const useTicketCategoryManagers = () => {
  return useQuery({
    queryKey: ['ticketCategoryManagers'],
    queryFn: () => SupabaseTicketCategoryManagerService.getAll()
  });
};

export const useTicketCategoryManagersByType = (ticketType: TicketType) => {
  return useQuery({
    queryKey: ['ticketCategoryManagers', ticketType],
    queryFn: () => SupabaseTicketCategoryManagerService.getByType(ticketType),
    enabled: !!ticketType
  });
};

export const useTicketCategoryManager = (id: string) => {
  return useQuery({
    queryKey: ['ticketCategoryManager', id],
    queryFn: () => SupabaseTicketCategoryManagerService.getById(id),
    enabled: !!id
  });
};

export const useCreateTicketCategoryManager = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<TicketCategoryManager, 'id' | 'created_at' | 'updated_at'>) =>
      SupabaseTicketCategoryManagerService.create(data),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ['ticketCategoryManagers'] });
    }
  });
};

export const useUpdateTicketCategoryManager = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<Omit<TicketCategoryManager, 'id' | 'created_at' | 'updated_at'>> }) =>
      SupabaseTicketCategoryManagerService.update(id, data),
    onSuccess: (_, variables) => {
      query_client.invalidateQueries({ queryKey: ['ticketCategoryManagers'] });
      query_client.invalidateQueries({ queryKey: ['ticketCategoryManager', variables.id] });
    }
  });
};

export const useDeleteTicketCategoryManager = () => {
  const query_client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => SupabaseTicketCategoryManagerService.delete(id),
    onSuccess: () => {
      query_client.invalidateQueries({ queryKey: ['ticketCategoryManagers'] });
    }
  });
};

export { SupabaseTicketCategoryManagerService };